import { apiSlice } from './apiSlice'

export const inventoryApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Products
    getProductCreateData: builder.query<any, { productId?: string | number } | void>({
      query: (args) => ({
        url: '/products/create',
        params: args?.productId ? { product_id: args.productId } : undefined,
      }),
      providesTags: ['Products', 'Warehouse'],
    }),
    getProducts: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/products',
        params: params || {},
      }),
      providesTags: ['Products'],
    }),
    getProductById: builder.query<any, string | number>({
      query: (id) => ({ url: `/products/${id}` }),
      providesTags: (result, error, id) => [{ type: 'Products', id }],
    }),
    getProductDetail: builder.query<any, string | number>({
      query: (id) => ({ url: `/get_product_detail/${id}` }),
      providesTags: (result, error, id) => [{ type: 'Products', id }],
    }),
    getProductWarehouses: builder.query<any, string | number>({
      query: (id) => ({ url: `/get_product_warehouses/${id}` }),
      providesTags: (result, error, id) => [{ type: 'Products', id }],
    }),
    getProductUnits: builder.query<any, string | number>({
      query: (id) => ({ url: `/get_product_units/${id}` }),
      providesTags: (result, error, id) => [{ type: 'Products', id }],
    }),
    getProductTransactions: builder.query<any, { id: string | number; params?: Record<string, any> }>({
      query: ({ id, params }) => ({
        url: `/get_product_transactions/${id}`,
        params: params || {},
      }),
      providesTags: (result, error, { id }) => [{ type: 'Products', id }],
    }),
    createProduct: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/products',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Products'],
    }),
    updateProduct: builder.mutation<any, { id: string | number; data: Record<string, any> }>({
      query: ({ id, data }) => {
        // PHP cannot parse multipart PUT bodies — send FormData as POST with an
        // appended `_method: 'PUT'` (method spoofing; purchasesApi has the same
        // guard). Without this the entire body is dropped and the update 422s.
        if (data instanceof FormData && !data.has('_method')) {
          data.append('_method', 'PUT');
        }
        return {
          url: `/products/${id}`,
          method: data instanceof FormData ? 'POST' : 'PUT',
          data,
        };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Products', id }, 'Products'],
    }),
    deleteProduct: builder.mutation<void, string | number>({
      query: (id) => ({
        url: `/products/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Products', id }, 'Products'],
    }),
    deleteMultipleProducts: builder.mutation<any, (number | string)[]>({
      query: (selectedIds) => ({
        url: '/products/delete/by_selection',
        method: 'POST',
        data: { selectedIds },
      }),
      invalidatesTags: ['Products'],
    }),
    importProducts: builder.mutation<any, FormData>({
      query: (data) => ({
        url: '/products/import/csv',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Products'],
    }),

    // Categories
    getCategories: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/categories',
        params: params || {},
      }),
      providesTags: ['Products'],
    }),
    createCategory: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/categories',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Products'],
    }),
    updateCategory: builder.mutation<any, { id: string | number; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/categories/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Products'],
    }),
    deleteCategory: builder.mutation<void, string | number>({
      query: (id) => ({
        url: `/categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Products'],
    }),
    deleteMultipleCategories: builder.mutation<any, (number | string)[]>({
      query: (selectedIds) => ({
        url: '/categories/delete/by_selection',
        method: 'POST',
        data: { selectedIds },
      }),
      invalidatesTags: ['Products'],
    }),

    // Asset/Expense/Deposit Categories
    createAssetCategory: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/fixed-assets/categories',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Products', 'AssetCategory'],
    }),
    updateAssetCategory: builder.mutation<any, { id: string | number; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/fixed-assets/categories/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Products', 'AssetCategory'],
    }),
    createExpenseCategory: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/expense_categories',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Products'],
    }),
    updateExpenseCategory: builder.mutation<any, { id: string | number; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/expense_categories/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Products'],
    }),
    createInventoryDepositCategory: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/deposits_category',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Products'],
    }),
    updateInventoryDepositCategory: builder.mutation<any, { id: string | number; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/deposits_category/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Products'],
    }),

    // Barcode
    getBarcodePageData: builder.query<any, void>({
      query: () => ({ url: '/barcode_create_page' }),
      providesTags: ['Products'],
    }),

    // Accounts (for forms)
    getAccountTypes: builder.query<any, void>({
      query: () => ({ url: '/account_types' }),
      providesTags: ['Accounting'],
    }),
    getAccountingSettings: builder.query<any, void>({
      query: () => ({ url: '/accounting_settings' }),
      providesTags: ['Accounting'],
    }),
    getAccountsList: builder.query<any, void>({
      query: () => ({ url: '/get_accounts' }),
      providesTags: ['Accounting'],
    }),

    // Brands
    getBrands: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/brands',
        params: params || {},
      }),
      providesTags: ['Products'],
    }),
    createBrand: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/brands',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Products'],
    }),
    updateBrand: builder.mutation<any, { id: string | number; data: Record<string, any> }>({
      query: ({ id, data }) => {
        // Same multipart-PUT problem as updateProduct: PHP drops the body.
        if (data instanceof FormData && !data.has('_method')) {
          data.append('_method', 'PUT');
        }
        return {
          url: `/brands/${id}`,
          method: data instanceof FormData ? 'POST' : 'PUT',
          data,
        };
      },
      invalidatesTags: ['Products'],
    }),
    deleteBrand: builder.mutation<void, string | number>({
      query: (id) => ({
        url: `/brands/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Products'],
    }),
    deleteMultipleBrands: builder.mutation<any, (number | string)[]>({
      query: (selectedIds) => ({
        url: '/brands/delete/by_selection',
        method: 'POST',
        data: { selectedIds },
      }),
      invalidatesTags: ['Products'],
    }),

    // Warehouses — tagged 'Warehouse', not 'Products': invalidating the
    // products list on every warehouse mutation used to nuke the (separately
    // cached) product catalog for no reason.
    getInventoryWarehouses: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/warehouses',
        params: params || {},
      }),
      providesTags: ['Warehouse'],
    }),
    createInventoryWarehouse: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/warehouses',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Warehouse'],
    }),
    updateInventoryWarehouse: builder.mutation<any, { id: string | number; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/warehouses/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Warehouse'],
    }),
    deleteInventoryWarehouse: builder.mutation<void, string | number>({
      query: (id) => ({
        url: `/warehouses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Warehouse'],
    }),

    // Units
    getUnits: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/units',
        params: params || {},
      }),
      providesTags: ['Products'],
    }),
    deleteUnit: builder.mutation<void, string | number>({
      query: (id) => ({
        url: `/units/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Products'],
    }),

    // Count Stock
    getCountStocks: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/countStock',
        params: params || {},
      }),
      providesTags: ['Products'],
    }),
    createCountStock: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/store_count_stock',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Products'],
    }),

    // Opening Inventory Accounts
    getOpeningAccounts: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/opening_inventory/accounts',
        params: params || {},
      }),
      providesTags: ['Products'],
    }),
    saveOpeningAccounts: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/opening_inventory/accounts/save',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Products'],
    }),

    // Transactions — backend: GET get_product_transactions/{id}
    getInventoryTransactions: builder.query<any, { productId: string | number; params?: Record<string, any> }>({
      query: ({ productId, params }) => ({
        url: `/get_product_transactions/${productId}`,
        params: params || {},
      }),
      providesTags: ['Products'],
    }),
    createInventoryAdjustment: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/adjustments',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Products'],
    }),
    createInventoryTransfer: builder.mutation<any, Record<string, any>>({
      query: (data) => ({
        url: '/transfers',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Products'],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetProductCreateDataQuery,
  useLazyGetProductCreateDataQuery,
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductByIdQuery,
  useGetProductDetailQuery,
  useGetProductWarehousesQuery,
  useGetProductUnitsQuery,
  useGetProductTransactionsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useDeleteMultipleProductsMutation,
  useImportProductsMutation,
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useDeleteMultipleCategoriesMutation,
  useCreateAssetCategoryMutation,
  useUpdateAssetCategoryMutation,
  useCreateExpenseCategoryMutation,
  useUpdateExpenseCategoryMutation,
  useCreateInventoryDepositCategoryMutation: useCreateDepositCategoryMutation,
  useUpdateInventoryDepositCategoryMutation: useUpdateDepositCategoryMutation,
  useGetBarcodePageDataQuery,
  useLazyGetBarcodePageDataQuery,
  useGetAccountTypesQuery,
  useLazyGetAccountTypesQuery,
  useGetAccountingSettingsQuery,
  useLazyGetAccountingSettingsQuery,
  useGetAccountsListQuery,
  useLazyGetAccountsListQuery,
  useGetBrandsQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useDeleteBrandMutation,
  useDeleteMultipleBrandsMutation,
  useGetInventoryWarehousesQuery: useGetWarehousesQuery,
  useCreateInventoryWarehouseMutation: useCreateWarehouseMutation,
  useUpdateInventoryWarehouseMutation: useUpdateWarehouseMutation,
  useDeleteInventoryWarehouseMutation: useDeleteWarehouseMutation,
  useGetUnitsQuery,
  useDeleteUnitMutation,
  useGetCountStocksQuery,
  useCreateCountStockMutation,
  useGetOpeningAccountsQuery,
  useSaveOpeningAccountsMutation,
  useGetInventoryTransactionsQuery,
  useCreateInventoryAdjustmentMutation: useCreateAdjustmentMutation,
  useCreateInventoryTransferMutation: useCreateTransferMutation,
} = inventoryApi
